import { setCurrentWorkspace } from '../actions/workspaceActions';
import { APP_INIT_RESPONSE_TYPE, CUSTOMER_MEMBER_ROLE, ONESIGNAL_APP_ID } from '../global/constants';
import { setWorkspaceIdInAxios, setupToken } from '../helpers/authTokenHelpers';
import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import { ROUTES } from '../routes/urls';
import { loadInboxData } from './inboxServices';
import { getInvoiceList } from './invoiceService';
import { getWorkspaceDetails, getWorkspaceList } from './workspaceService';
import { getColorWithOpacity, getCurrentDomain, isDev, isEmpty, isReactNative } from '../helpers/common';
import TagManager from 'react-gtm-module';
import { setAnlayticUserAndCompany } from './analyticsService';
import { captureException, setLogUser } from './logService';
import { CustomWindow } from '../interfaces/OneSignalInterface';
import { getTotalTaskCount } from './taskServices';
import { getUserPreferenceData } from '../helpers/firebaseHelper';
import { fetchGraphQL } from '../lib/contentful';
import { auth } from '../utils/firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { getFirebaseToken } from './authServices';
import { getCompanySettingIntegration, getProfileSettingData, getSubscriptionList } from './settingServices';
import _ from 'lodash';
import { AnyAction, Dispatch } from 'redux';
import { connectToWebsocket } from './realtimeServices';
import { rootStore } from '../mobx/rootStore';
import { syncProjects, syncTasks } from './syncService';
import { getViewList } from './viewServices';

declare const window: CustomWindow;

/**
 * Fetches and applies branding data based on the current hostname
 * This function queries Contentful for client configuration and applies
 * custom branding including colors, favicon, and title
 *
 * @param dispatch - Redux dispatch function
 */
export const getBrandDataBasedOnHostname: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    const hostName = getCurrentDomain();
    // GraphQL query to fetch client configuration
    const query = `query{
        clientConfigurationCollection(where:{domain: "${hostName}"}){
          items{
            primaryColor
            primaryDarkColor
            domain
            faviconIcon{
              url
            }
            metaTitle
            logo{
              url
            }
          }
        }
      }`;
    const response = await fetchGraphQL(query);
    const customBrandingData = response?.data?.clientConfigurationCollection?.items?.[0];

    if (customBrandingData) {
      // Apply custom branding if available
      applyCustomBranding(customBrandingData, dispatch);
    } else {
      // Apply default branding
      applyDefaultBranding(dispatch);
    }
  } catch (e) {
    captureException(e);
  }
};

/**
 * Applies custom branding data to the application
 * @param customBrandingData - The branding data to apply
 * @param dispatch - Redux dispatch function
 */
const applyCustomBranding = (customBrandingData: any, dispatch: Dispatch<AnyAction>): void => {
  rootStore.appStore.setCustomBranding(customBrandingData);

  // Set document title
  document.title = !isEmpty(customBrandingData?.metaTitle) ? customBrandingData?.metaTitle : 'Teamcamp';

  // Set favicon
  updateFavicon(customBrandingData?.faviconIcon?.url);

  // Apply light mode colors
  applyLightModeColors(customBrandingData?.primaryColor);

  // Apply dark mode colors
  applyDarkModeColors(customBrandingData?.primaryDarkColor);
};

/**
 * Updates the favicon with the provided URL or default
 * @param faviconUrl - URL of the custom favicon
 */
const updateFavicon = (faviconUrl?: string): void => {
  const favicon: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
  const defaultFavicon =
    'https://images.ctfassets.net/6v3c95narsix/4meyMh26aF5gp7IGa3WUmp/028b802e6183801e3b809eca10644fdb/Union.svg';

  if (favicon) {
    favicon.href = faviconUrl || defaultFavicon;
  }
};

/**
 * Applies light mode color theme
 * @param primaryColor - Primary color for light mode
 */
const applyLightModeColors = (primaryColor?: string): void => {
  const root = document.querySelector('.light') as HTMLElement;
  if (!root) return;

  if (primaryColor) {
    const hoverColor = getColorWithOpacity(primaryColor);
    root.style.setProperty('--brand-primary', primaryColor);
    root.style.setProperty('--brand-primary-hover', !isEmpty(hoverColor) ? hoverColor : primaryColor);
  } else {
    // Default colors
    root.style.setProperty('--brand-primary', '#00A16F');
    root.style.setProperty('--brand-primary-hover', '#03724F');
  }
};

/**
 * Applies dark mode color theme
 * @param primaryDarkColor - Primary color for dark mode
 */
const applyDarkModeColors = (primaryDarkColor?: string): void => {
  const darkRoot = document.querySelector('.dark') as HTMLElement;
  if (!darkRoot) return;

  if (primaryDarkColor) {
    const hoverColor = getColorWithOpacity(primaryDarkColor);
    darkRoot.style.setProperty('--brand-primary', primaryDarkColor);
    darkRoot.style.setProperty('--brand-primary-hover', !isEmpty(hoverColor) ? hoverColor : primaryDarkColor);
  } else {
    // Default colors
    darkRoot.style.setProperty('--brand-primary', '#00A16F');
    darkRoot.style.setProperty('--brand-primary-hover', '#03724F');
  }
};

/**
 * Applies default branding when no custom branding is available
 * @param dispatch - Redux dispatch function
 */
const applyDefaultBranding = (dispatch: Dispatch<AnyAction>): void => {
  const favicon: HTMLLinkElement | null = document.querySelector('link[rel="icon"]');
  if (favicon?.href) {
    favicon.href =
      'https://images.ctfassets.net/6v3c95narsix/4meyMh26aF5gp7IGa3WUmp/028b802e6183801e3b809eca10644fdb/Union.svg';
  }

  document.title = 'Teamcamp';
  rootStore.appStore.setCustomBranding({});

  // Apply default light mode colors
  const root = document.querySelector('.light');
  if (root && root instanceof HTMLElement) {
    root.style.setProperty('--brand-primary', '#00A16F');
    root.style.setProperty('--brand-primary-hover', '#03724F');
  }

  // Apply default dark mode colors
  const darkRoot = document.querySelector('.dark');
  if (darkRoot && darkRoot instanceof HTMLElement) {
    darkRoot.style.setProperty('--brand-primary', '#00A16F');
    darkRoot.style.setProperty('--brand-primary-hover', '#03724F');
  }
};

/**
 * Fetches and applies branding data based on workspace ID
 * @param workspaceId - The ID of the workspace
 * @param dispatch - Redux dispatch function
 */
export const getBrandDataBasedOnWorkspaceId: any = (workspaceId: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    if (isEmpty(workspaceId)) return;

    // GraphQL query to fetch client configuration for workspace
    const query = `query{
        clientConfigurationCollection(where:{workspaceId: "${workspaceId}"}){
          items{
            primaryColor
            primaryDarkColor
            domain
            faviconIcon{
              url
            }
            metaTitle
            logo{
              url
            }
          }
        }
      }`;
    const response = await fetchGraphQL(query);
    const customBrandingData = response?.data?.clientConfigurationCollection?.items?.[0];

    if (customBrandingData) {
      // Apply custom branding if available
      applyCustomBranding(customBrandingData, dispatch);
    } else {
      // Fall back to hostname-based branding
      const hostName = getCurrentDomain();
      if (hostName === 'dash.teamcamp.app') {
        applyDefaultBranding(dispatch);
      } else {
        dispatch(getBrandDataBasedOnHostname());
      }
    }
  } catch (e) {
    captureException(e);
  }
};

/**
 * Loads Firebase token for a workspace
 * @param workspaceId - The ID of the workspace
 * @param dispatch - Redux dispatch function
 */
const loadWorkspaceFirebaseToken = (workspaceId: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    const firebaseToken = await dispatch(getFirebaseToken(workspaceId));
    const firebaseResult: any = await signInWithCustomToken(auth, firebaseToken);

    // Store Firebase token in user preferences
    UserPreferenceSingleton.getInstance().setFirebaseToken({
      token: firebaseResult.user?.accessToken,
      workspaceId: workspaceId
    });

    // Set up token refresh listeners
    setupFirebaseTokenListeners(workspaceId);
  } catch (e) {
    console.log('Error ', e);
    captureException(e);
  }
};

/**
 * Sets up Firebase token refresh listeners
 * @param workspaceId - The ID of the workspace
 */
const setupFirebaseTokenListeners = (workspaceId: string): void => {
  // Listen for ID token changes
  auth.onIdTokenChanged(async (userDetails) => {
    if (userDetails && !_.isNull(userDetails)) {
      const user = auth.currentUser;
      const token = await user?.getIdToken();
      UserPreferenceSingleton.getInstance().setFirebaseToken({ token, workspaceId: workspaceId });
    }
  });

  // Listen for auth state changes
  auth.onAuthStateChanged(async (userDetails) => {
    if (userDetails && !_.isNull(userDetails)) {
      const user = auth.currentUser;
      const token = await user?.getIdToken();
      UserPreferenceSingleton.getInstance().setFirebaseToken({ token, workspaceId: workspaceId });
    }
  });
};

/**
 * Loads initial workspace details
 * @param workspaceId - The ID of the workspace
 * @param dispatch - Redux dispatch function
 * @returns Project list response
 */
export const loadInitWorkspaceDetails: any = (workspaceId: string) => async (dispatch: Dispatch<any>) => {
  try {
    rootStore.appStore.setIsAppLoading(true);
    if (workspaceId) {
      dispatch(loadWorkspaceFirebaseToken(workspaceId));
      await syncProjects({ loadCached: true });
      return rootStore.projectStore.projects || [];
    }
  } catch (e) {
    console.log('Error from load workspace details');
  } finally {
    rootStore.appStore.setIsAppLoading(false);
  }
};

/**
 * Initializes a workspace based on user preferences or environment
 * @param dispatch - Redux dispatch function
 * @param checkForLastVisitedWorkspace - Whether to check for last visited workspace
 * @returns Workspace details
 */
const initializeWorkspace = async (dispatch: Dispatch<any>, checkForLastVisitedWorkspace: boolean) => {
  // Get workspace list and use the first one
  await dispatch(getWorkspaceList());

  // Check for last visited workspace in production
  if (checkForLastVisitedWorkspace && !isDev()) {
    // Get last visited workspace id from user preference
    const lastVisitedWorkspace = await getUserPreferenceData('current_workspace_id');
    if (!isEmpty(lastVisitedWorkspace)) {
      return await dispatch(getWorkspaceDetails(lastVisitedWorkspace));
    }
  }

  // Use dev workspace in development environment
  if (isDev()) {
    const devWorkspace = UserPreferenceSingleton.getInstance().getWorkspace();
    if (devWorkspace) return await dispatch(getWorkspaceDetails(devWorkspace?.id));
  }

  // Get workspace list and use the first one
  await dispatch(getWorkspaceList());
  return UserPreferenceSingleton.getInstance().getWorkspace();
};

/**
 * Initializes the application
 * @param isInitLoad - Whether this is the initial load
 * @param checkForLastVisitedWorkspace - Whether to check for last visited workspace
 * @param dispatch - Redux dispatch function
 * @returns Response type and redirect path if needed
 */
export const appInit: any =
  (isInitLoad = false, checkForLastVisitedWorkspace = true) =>
  async (dispatch: Dispatch<any>) => {
    try {
      rootStore.appStore.setIsAppLoading(true);

      // Initialize Google Tag Manager
      const tagManagerArgs = {
        gtmId: 'GTM-N8SMZXPX'
      };
      TagManager.initialize(tagManagerArgs);

      // Check authentication token
      const token = setupToken();
      if (!token) return;

      // Get user details and set up analytics
      const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
      if (userDetails) {
        setLogUser(userDetails);
        if (isInitLoad) dispatch(getProfileSettingData());

        // Set up FCM for notifications
        setupFCMNotifications(userDetails);
      }

      // Initialize workspace
      const workspaceDetails = await initializeWorkspace(dispatch, checkForLastVisitedWorkspace);
      if (!workspaceDetails) return { type: APP_INIT_RESPONSE_TYPE.REDIRECT, path: ROUTES.WORKSPACE };
      const isExtractedView = await getUserPreferenceData('extractedDesktopView');
      rootStore.appStore.setExtractedDesktopView(isExtractedView);
      // Set up workspace in stores and services
      setupWorkspace(workspaceDetails, userDetails, dispatch);

      // Load data for the workspace
      await Promise.all([
        dispatch(getBrandDataBasedOnWorkspaceId(workspaceDetails?.id)),
        dispatch(setCurrentWorkspace(workspaceDetails)),
        setAnlayticUserAndCompany(userDetails, workspaceDetails),
        syncTasks({ loadCached: true }),
        syncProjects({ loadCached: true }),
        getViewList()
      ]);

      // Load additional data if not on home page
      if (window.location.pathname !== '/') {
        dispatch(loadInboxData());
      }

      // Load workspace settings and data
      await loadWorkspaceData(workspaceDetails, userDetails, dispatch);

      const projectResponse = rootStore.projectStore.projects || [];

      // Check if user should be redirected to welcome page
      if (
        projectResponse?.length === 0 &&
        workspaceDetails?.customerRole !== CUSTOMER_MEMBER_ROLE.PROJECT_ONLY &&
        workspaceDetails?.customerRole !== CUSTOMER_MEMBER_ROLE.FULL_ACCESS &&
        (workspaceDetails?.isAdmin || workspaceDetails?.isOwner)
      ) {
        return { type: APP_INIT_RESPONSE_TYPE.SUCCESS };
      } else {
        return { type: APP_INIT_RESPONSE_TYPE.SUCCESS };
      }
    } catch (e) {
      console.log('Error from load workspace details', e);
    } finally {
      rootStore.appStore.setIsAppLoading(false);
    }
  };

/**
 * Sets up FCM notifications for the user
 * @param userDetails - User details
 */
const setupFCMNotifications = (userDetails: any): void => {
  if (window.electronApi?.subscribeFcm) {
    window.electronApi.subscribeFcm();
  }

  if (isReactNative()) {
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'subscribe-fcm', data: { accessToken: userDetails.access_token } })
    );
  }
};

/**
 * Sets up workspace in stores and services
 * @param workspaceDetails - Workspace details
 * @param userDetails - User details
 * @param dispatch - Redux dispatch function
 */
const setupWorkspace = (workspaceDetails: any, userDetails: any, dispatch: Dispatch<any>): void => {
  rootStore.workspaceStore.setCurrentWorkspace(workspaceDetails);
  setWorkspaceIdInAxios(workspaceDetails?.id);
  dispatch(loadWorkspaceFirebaseToken(workspaceDetails?.id));

  rootStore.setCurrentUser(userDetails);
};

/**
 * Loads workspace data and settings
 * @param workspaceDetails - Workspace details
 * @param userDetails - User details
 * @param dispatch - Redux dispatch function
 */
const loadWorkspaceData = async (workspaceDetails: any, userDetails: any, dispatch: Dispatch<any>): Promise<void> => {
  dispatch(getCompanySettingIntegration(workspaceDetails?.id));
  dispatch(getTotalTaskCount());
  connectToWebsocket(workspaceDetails?.id, userDetails?.id);
  dispatch(getSubscriptionList());

  if (workspaceDetails?.isAdmin === true) {
    dispatch(getInvoiceList(workspaceDetails));
  }
};

/**
 * Initializes push notifications using OneSignal
 */
export const initPushNotification = async () => {
  try {
    const OneSignal = window.OneSignal || [];
    if (!OneSignal) return;

    if (!ONESIGNAL_APP_ID) return;
    if (!window.location.origin.startsWith('https://dash.teamcamp.app')) return;

    const onesignalInit = UserPreferenceSingleton.getInstance().getOneSignalInit();
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();

    // Initialize OneSignal if not already initialized
    if (!onesignalInit) {
      await initializeOneSignal();
    }

    // Set user tags if user is logged in
    if (!userDetails) return;
    setUserTags(userDetails);

    // Set up notification URL and event handlers
    setupNotificationHandlers();
  } catch (e) {
    captureException(e);
    console.log('ERROR', e);
  }
};

/**
 * Initializes OneSignal for push notifications
 */
const initializeOneSignal = async (): Promise<void> => {
  await window.OneSignal?.push(async function () {
    await window.OneSignal.init({
      appId: ONESIGNAL_APP_ID,
      autoRegister: true,
      notifyButton: {
        enable: false
      }
    })
      .then(async () => {
        UserPreferenceSingleton.getInstance().setOneSignalInit(true);
        window.OneSignal.showSlidedownPrompt();
      })
      .catch((error: any) => {
        captureException(error);
        console.log('OneSignal Error', error);
      });
  });
};

/**
 * Sets user tags for OneSignal
 * @param userDetails - User details
 */
const setUserTags = (userDetails: any): void => {
  const userTagPayload = {
    email: userDetails.email,
    userid: userDetails.id
  };

  try {
    window.OneSignal?.push(function () {
      window.OneSignal.sendTags(userTagPayload)
        .then(function (tagsSent: any) {})
        .catch(function (error: any) {
          console.error('Error sending tags:', error);
        });
    });
  } catch (e) {
    captureException(e);
    console.log('Error in OneSignle-sendTags', e);
  }
};

/**
 * Sets up notification URL and event handlers
 */
const setupNotificationHandlers = (): void => {
  try {
    // Set default notification URL
    let notificationUrl = 'https://dash.teamcamp.app';
    if (isDev()) {
      notificationUrl = 'http://localhost:8080';
    }
    window.OneSignal?.push(function () {
      window.OneSignal.setDefaultNotificationUrl(notificationUrl);
    });
  } catch (e) {
    captureException(e);
    console.log('Error in OneSignle-GetPushNotificationPermission', e);
  }

  // Set up notification event handlers
  window.OneSignal?.push(function () {
    window.OneSignal.isPushNotificationsEnabled(function (isEnabled: boolean) {});
  });

  window.OneSignal?.push(function () {
    window.OneSignal.on('notificationDisplay', function (event: any) {});
  });

  window.OneSignal?.push(function () {
    window.OneSignal.on('notificationClicked', function (event: any) {
      console.log('Notification clicked:', event);
    });
  });
};

export function dispatchAppError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

export function dispatchAppSuccess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
